<template>
	<div v-if="anyActionAvailable" class="batch">
		<div class="info">
			<h3>Batch controls</h3>
			<p>These actions only apply to submissions in the current tab.</p>
		</div>
		<div class="buttons">
			<IconButton
				v-if="canApprove"
				label="Approve all"
				:disabled="loading"
				@click="onApprove"
			>
				<IconApprove />
			</IconButton>
			<IconButton
				v-if="canDecline"
				label="Decline all"
				:disabled="loading"
				@click="onDecline"
			>
				<IconDecline />
			</IconButton>
			<IconButton
				v-if="canToggleShortlist"
				label="Shortlist all"
				:disabled="loading"
				@click="onPromote"
			>
				<IconPromote />
			</IconButton>
			<IconButton
				v-if="canToggleShortlist"
				label="Unshortlist all"
				:disabled="loading"
				@click="onDemote"
			>
				<IconDemote />
			</IconButton>
			<IconButton
				v-if="canToggleVisibility"
				label="Show all in gallery"
				:disabled="loading"
				@click="onShow"
			>
				<IconShow />
			</IconButton>
			<IconButton
				v-if="canToggleVisibility"
				label="Hide all from gallery"
				:disabled="loading"
				@click="onHide"
			>
				<IconHide />
			</IconButton>
		</div>
	</div>
</template>

<script>
import { enums } from 'protoswipe-shared'
import IconButton from '@/components/admin/IconButton'

import IconApprove from 'mdi-vue/Check'
import IconDecline from 'mdi-vue/Close'
import IconPromote from 'mdi-vue/PlaylistPlus'
import IconDemote from 'mdi-vue/PlaylistRemove'
import IconShow from 'mdi-vue/Eye'
import IconHide from 'mdi-vue/EyeOff'

const { SubmissionStatus } = enums

export default {
	name: 'SubmissionBatchControls',
	props: {
		campaign: Object,
		loading: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		queryStatus() {
			return this.$route.query.status || null
		},
		canApprove() {
			return this.queryStatus !== SubmissionStatus.approved
		},
		canDecline() {
			return this.queryStatus !== SubmissionStatus.declined
		},
		canToggleShortlist() {
			return (
				this.$route.name === 'Current Campaign' &&
				(this.queryStatus === SubmissionStatus.approved ||
					this.queryStatus === SubmissionStatus.declined)
			)
		},
		canToggleVisibility() {
			return this.queryStatus === SubmissionStatus.approved
		},
		anyActionAvailable() {
			return (
				this.canApprove ||
				this.canDecline ||
				this.canToggleShortlist ||
				this.canToggleVisibility
			)
		}
	},
	methods: {
		onApprove() {
			this.update(
				{
					status: SubmissionStatus.approved,
					round: this.campaign.round
				},
				'Successfully approved all submissions.',
				'Failed to approve all submissions.'
			)
		},
		onDecline() {
			this.update(
				{
					status: SubmissionStatus.declined,
					promote: false,
					round: this.campaign.round
				},
				'Successfully declined all submissions.',
				'Failed to decline all submissions.'
			)
		},
		onPromote() {
			this.update(
				{ promote: true },
				'Successfully added all submissions to shortlist.',
				'Failed to add all submissions to shortlist.'
			)
		},
		onDemote() {
			this.update(
				{ promote: false },
				'Successfully removed all submissions from shortlist.',
				'Failed to remove all submissions from shortlist.'
			)
		},
		onShow() {
			this.update(
				{ hideFromGallery: false },
				'All submissions will be shown in gallery.',
				'Failed to add all submissions to gallery.'
			)
		},
		onHide() {
			this.update(
				{ hideFromGallery: true },
				'All submissions will be hidden from gallery.',
				'Failed to hide all submissions from gallery.'
			)
		},
		update(update, successMsg, errorMsg) {
			const query = { ...this.$route.query, campaign: this.campaign.id }
			this.$emit('update', { query, update, successMsg, errorMsg })
		}
	},
	components: {
		IconButton,
		IconApprove,
		IconDecline,
		IconPromote,
		IconDemote,
		IconShow,
		IconHide
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/layout';

.batch {
	margin-top: 3rem;
	padding: 1.5rem;
	background-color: $blue-98;
}

.info {
	@include truncate-margin;
	margin-bottom: 1.5rem;

	> * {
		margin: 0;
		+ * {
			margin-top: 0.5rem;
		}
	}
}

.buttons {
	display: flex;
	flex-wrap: wrap;
	margin-bottom: -1rem;

	> * {
		margin-bottom: 1rem;
		&:not(:last-child) {
			margin-right: 1rem;
		}
	}
}
</style>
